import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  Avatar,
  Row,
  Col,
  Typography,
  Space,
  Select,
} from "antd";
import { PhoneOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useLearnerProfile from "PlatForms/OrgLMS/Hooks/LearnerHooks/useLearnerProfile";
import { useCourseList } from "PlatForms/OrgLMS/Hooks/AdminHooks/useCourseList";
import { Country, State, City } from "country-state-city";
const { Text } = Typography;
const { Option } = Select;
const LearnerProfile = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { businessGroups } = useCourseList();
  // Country, State, City States
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
  }, []);
  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    const relatedStates = State.getStatesOfCountry(value);
    setStates(relatedStates);
    setCities([]);
    form.setFieldsValue({ state: undefined, city: undefined });
  };
  const handleStateChange = (value) => {
    setSelectedState(value);
    const relatedCities = City.getCitiesOfState(selectedCountry, value);
    setCities(relatedCities);
    form.setFieldsValue({ city: undefined });
  };
  const {
    profileData,
    loading,
    updating,
    imageUrl,
    fileList,
    beforeUpload,
    handleImageChange,
    updateProfile,
  } = useLearnerProfile(form);
  // Find the current business group name
  const currentBusinessGroup = businessGroups.find(
    group => group.id === profileData?.businessGroupId
  )?.name || profileData?.businessGroupName || '';
  return (
    <Card
      loading={loading}
      title="Learner Profile"
      className="card-professional"
    >
      <Form form={form} layout="vertical" onFinish={(values) => updateProfile(values, navigate)}>
        <Row gutter={[32, 16]} align="middle">
          <Col xs={24} md={6} style={{ textAlign: "center" }}>
            <Form.Item name="imageFile">
              <Upload
                name="imageFile"
                listType="picture-card"
                showUploadList={false}
                beforeUpload={beforeUpload}
                onChange={handleImageChange}
                fileList={fileList}
                accept="image/*"
              >
                {imageUrl ? (
                  <Avatar src={imageUrl} size={110} style={{ border: "2px solid #eee" }} />
                ) : (
                  <Avatar
                    icon={<UserOutlined />}
                    size={110}
                    style={{ backgroundColor: "#f0f0f0" }}
                  />
                )}
              </Upload>
              <Text type="secondary">Click to change</Text>
            </Form.Item>
          </Col>
          <Col xs={24} md={18}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[{ required: true, message: "Please input your first name!" }]}
                >
                  <Input placeholder="John" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[{ required: true, message: "Please input your last name!" }]}
                >
                  <Input placeholder="Doe" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Email">
                  <Input value={profileData?.email || ""} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Phone Number"
                  name="phoneNo"
                  rules={[
                    { required: true, message: "Please enter your phone number!" },
                    {
                      pattern: /^\+?[0-9\s]*$/,
                      message: "Please enter only numbers and optional + prefix!",
                    },
                  ]}
                >
                  <Input
                    type="tel"
                    prefix={<PhoneOutlined />}
                    placeholder="+1234567890 or 0312345678"
                    onKeyPress={(e) => {
                      if (!/[0-9+]/.test(e.key) && e.key !== "Backspace") e.preventDefault();
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      if ((value.match(/\+/g) || []).length > 1) {
                        e.target.value = value.replace(/\+/g, "");
                        if (value.startsWith("+")) e.target.value = "+" + e.target.value;
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              {/* Business Group as read-only text */}
              <Col xs={24} md={12}>
                <Form.Item label="Business Group">
                  <Input
                    value={currentBusinessGroup}
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Country"
                  name="country"
                  rules={[{ required: true, message: "Please select your country!" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select your country"
                    optionFilterProp="children"
                    onChange={handleCountryChange}
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={countries.map((country) => ({
                      label: country.name,
                      value: country.isoCode,
                    }))}
                  />
                </Form.Item>
              </Col>
              {/* State */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="State"
                  name="state"
                  rules={[
                    {
                      required: selectedCountry === "US",
                      message: "State is required when country is United States!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Select your state"
                    optionFilterProp="children"
                    onChange={handleStateChange}
                    disabled={!selectedCountry}
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={states.map((state) => ({
                      label: state.name,
                      value: state.isoCode,
                    }))}
                  />
                </Form.Item>
              </Col>
              {/* City */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="City"
                  name="city"
                  rules={[{ required: true, message: "Please select your city!" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select your city"
                    optionFilterProp="children"
                    disabled={!selectedState}
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={cities.map((city) => ({
                      label: city.name,
                      value: city.name,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" loading={updating}>
                      Update Profile
                    </Button>
                    <Button onClick={() => form.resetFields()} disabled={updating}>
                      Reset
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};
export default LearnerProfile;